import React, { useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { prefixClass } from 'lib/utils';
import { IconFolder } from 'components/Icons';
import { MobileSlideUpPanel } from '../MobileSlideUpPanel/MobileSlideUpPanel';
import type { GroupItem } from '../../../Dashboard/useGroups';
import './MobileGroupPickerPanel.scss';

interface MobileGroupPickerPanelProps {
  isOpen: boolean;
  onClose: () => void;
  groups: GroupItem[];
  activeGroupId: string | null;
  onGroupSelect: (groupId: string) => void;
  /** Changes title and behavior — "Add to group" vs "Groups" */
  mode?: 'browse' | 'assign';
  /** Exclude a specific group from the list (e.g. current group when moving) */
  excludeGroupId?: string;
}

export const MobileGroupPickerPanel: React.FC<MobileGroupPickerPanelProps> = React.memo(
  ({ isOpen, onClose, groups, activeGroupId, onGroupSelect, mode = 'browse', excludeGroupId }) => {
    const prefix = prefixClass('ksq2-mobile-group-picker');

    const filteredGroups = useMemo(
      () => (excludeGroupId ? groups.filter(g => g.id !== excludeGroupId) : groups),
      [groups, excludeGroupId]
    );

    const handleGroupClick = useCallback(
      (groupId: string) => {
        onGroupSelect(groupId);
        onClose();
      },
      [onGroupSelect, onClose]
    );

    const title = mode === 'assign' ? 'Add to group' : 'Groups';

    return (
      <MobileSlideUpPanel
        isOpen={isOpen}
        onClose={onClose}
        title={title}
        titleIcon={<IconFolder />}
        useFitContent
        resizable={false}
      >
        <div className={prefix()}>
          {filteredGroups.length === 0 ? (
            <div className={prefix('__empty')}>No groups available</div>
          ) : (
            <div className={prefix('__list')}>
              {filteredGroups.map(group => (
                <button
                  key={group.id}
                  type="button"
                  className={classNames(prefix('__item'), {
                    [prefix('__item--active')]: mode === 'browse' && activeGroupId === group.id,
                  })}
                  onClick={() => handleGroupClick(group.id)}
                >
                  <div className={prefix('__item-left')}>
                    <span
                      className={classNames(prefix('__item-name'), {
                        [prefix('__item-name--empty')]: !group.requestCount,
                      })}
                    >
                      {group.name}
                    </span>
                  </div>
                  <span className={prefix('__item-count')}>{group.requestCount}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </MobileSlideUpPanel>
    );
  }
);

MobileGroupPickerPanel.displayName = 'MobileGroupPickerPanel';

export default MobileGroupPickerPanel;
